import useCurrentUserAccount from '../../useCurrentUserAccount';
import useDriftClient from '../../useDriftClient';
import { useCallback, useMemo } from 'react';
import {
	createSwiftMarketOrder,
	UIMarket,
	createSwiftLimitOrder,
	UIOrderType,
	COMMON_UI_UTILS,
	MINIMUM_SWIFT_ORDER_SIGNING_EXPIRATION_BUFFER_SLOTS,
	SWIFT_ORDER_SIGNING_EXPIRATION_BUFFER_SLOTS,
	DriftErrors,
} from '@drift/common';
import {
	getNextUserOrderIdForCurrentUser,
	ORDER_PREP_UTILS,
} from 'src/stores/DriftStore/actions/actionHelpers/orderPrep';
import { PerpTradeFormOutputProps } from 'src/stores/DriftStore/actions/actionHelpers/types';
import { TRADE_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/tradePrep';
import invariant from 'tiny-invariant';
import {
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	FOUR,
	User,
	MAX_LEVERAGE_ORDER_SIZE,
	digestSignature,
	SLOT_TIME_ESTIMATE_MS,
	PositionDirection,
	PostOnlyParams,
} from '@drift-labs/sdk';
import useWalletContext from '../../useWalletContext';
import Env, {
	syncGetCurrentSettings,
	UserSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import { DriftWindow } from 'src/window/driftWindow';
import { dlog } from 'src/dev';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import usePostHogCapture from '../../posthog/usePostHogCapture';
import {
	AnchorError,
	createAnchorErrorFromSwiftMessage,
	isLedgerSignMessageError,
	tryGetDriftErrorCode,
} from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { notify } from 'src/utils/notifications';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useTxErrorHandler } from 'src/hooks/driftActions/transactions/useTxErrorHandler';
import { useGetMarkPriceForMarket } from 'src/hooks/useMarkPrice';
import useCurrentSlotRef from 'src/hooks/useCurrentSlotRef';
import { SignedMsgToastEvent } from 'src/@types/signedMsgOrders';
import { checkMaxLeverageFlagUsage } from 'src/utils/trade/checkMaxLeverageFlagUsage';
import { OrderFillTimeTracker } from 'src/utils/orderFillTimeTracker';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import {
	LEDGER_SIGN_OFF_CHAIN_MESSAGE_ERROR_MESSAGE,
	SWIFT_ORDER_SIGNATURE_VERIFICATION_ERROR_MESSAGE,
} from 'src/utils/TransactionErrorHandler/consts';
import { useNetworkInfoStore } from 'src/stores/useNetworkInfoStore';
import { getEffectiveTypeTimeoutMultiplier } from 'src/utils/signedMsgs';
import { logOrderParams } from 'src/utils/logging/auctionParams';
import { useWalletsV2 } from 'src/hooks/wallets/useWalletsV2';

const DRIFT_UI_SWIFT_ORDER_SOURCE = 'drift-ui';

// Fallback when DriftErrors.errorsList.InsufficientCollateral is unavailable (e.g. build/import variance); 6003 is the code for InsufficientCollateral in drift program.
const INSUFFICIENT_COLLATERAL_FALLBACK_CODE = 6003;
const INSUFFICIENT_COLLATERAL_ERROR_CODE =
	DriftErrors.errorsList.InsufficientCollateral?.code ??
	INSUFFICIENT_COLLATERAL_FALLBACK_CODE;

interface SwiftOrderResult {
	sent: boolean;
	retryWithDepositBuffer: boolean;
}

const trackSwiftOrderResult = () => {
	let resultPromiseResolver: ((value: SwiftOrderResult) => void) | null;
	let isHandled = false;

	const resultPromise = new Promise<SwiftOrderResult>((resolve) => {
		resultPromiseResolver = resolve;
	});

	const handleResult = (result: SwiftOrderResult) => {
		if (resultPromiseResolver && !isHandled) {
			resultPromiseResolver(result);
			isHandled = true;
		}
	};

	return {
		handleResult,
		resultPromise,
	};
};

export const useOpenSwiftOrders = () => {
	const driftClient = useDriftClient();
	const currentUserAccount = useCurrentUserAccount();
	const wallet = useWalletContext();
	const appEventEmitter = useDriftStore((s) => s.appEventEmitter);
	const currentSlotRef = useCurrentSlotRef();
	const networkState = useNetworkInfoStore((s) => s.effectiveType);

	const { captureEvent } = usePostHogCapture();
	const getOraclePrice = useGetOraclePriceForMarket();
	const handleTxError = useTxErrorHandler();
	const getMarkPrice = useGetMarkPriceForMarket();

	const {
		authorityWallet,
		preferredTradingSignerWallet,
		isEmbeddedWalletThePreferredTradingSigner,
	} = useWalletsV2(true);

	const logSwiftError = useCallback(
		(e: unknown, swiftDebugProps: any) => {
			console.error(e);
			captureEvent('swift_error', { ...swiftDebugProps, error: e });

			const isDevMode = window?.localStorage?.getItem('devswitch') === 'true';
			if (isDevMode) {
				console.log('SWIFT Error: SWIFT props', swiftDebugProps);
			}
		},
		[captureEvent]
	);

	const swiftConfirmationMultiplier = useMemo(() => {
		return getEffectiveTypeTimeoutMultiplier(networkState);
	}, [networkState]);

	const openSwiftMarketOrder = useCallback(
		async (
			perpTradeFormOutputProps: PerpTradeFormOutputProps & {
				leadSide: 'base' | 'quote';
			},
			highLeverageSpotsOpen?: number
		): Promise<{
			sent: boolean;
			swiftUuid: string;
			retryWithDepositBuffer: boolean;
		}> => {
			invariant(driftClient, 'driftClient is required');
			invariant(currentUserAccount?.client, 'currentUserAccount is required');
			invariant(authorityWallet, 'authorityWallet is required');
			invariant(
				preferredTradingSignerWallet,
				'preferredTradingSignerWallet is required'
			);

			const {
				direction,
				baseAmountBigNum,
				bracketOrders,
				numOfOrders,
				optionalAuctionParamsInputs,
			} = ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps(
				perpTradeFormOutputProps
			);

			const uiMarket = UIMarket.createPerpMarket(
				perpTradeFormOutputProps.targetMarketIndex
			);
			const marketId = uiMarket.marketId;
			const settings = syncGetCurrentSettings() as UserSettings;
			const nextUserOrderId = getNextUserOrderIdForCurrentUser();

			const signedMsgOrderToastEventProps =
				TRADE_PREP_UTILS.getSignedMsgOrderToastEventProps({
					marketId,
					direction,
					baseSize: baseAmountBigNum,
					numOrders: numOfOrders,
					currentSettings: settings as Partial<UserSettings>,
					bracketOrders: perpTradeFormOutputProps.bracketOrders,
					orderType: perpTradeFormOutputProps.orderType,
					userOrderId: nextUserOrderId,
				});

			const oraclePrice = getOraclePrice(marketId);
			const markPrice = getMarkPrice(marketId).toNum();

			const swiftDebugProps = getSwiftDebugProps(
				perpTradeFormOutputProps,
				currentUserAccount.client,
				'', // uuid populated in onSigningSuccess
				uiMarket.symbol,
				oraclePrice.toNum(),
				markPrice,
				settings,
				currentSlotRef.current,
				bracketOrders,
				signedMsgOrderToastEventProps
			);

			// safety check for max leverage flag usage
			const useMaxLeverage = checkMaxLeverageFlagUsage(
				!!perpTradeFormOutputProps.maxLeverageSelected,
				perpTradeFormOutputProps.targetMarketIndex,
				baseAmountBigNum,
				oraclePrice,
				currentUserAccount.client,
				direction,
				perpTradeFormOutputProps.displayLeverage,
				highLeverageSpotsOpen
			);
			const baseAmountToUseBN = useMaxLeverage
				? MAX_LEVERAGE_ORDER_SIZE
				: baseAmountBigNum.val;

			try {
				const { handleResult, resultPromise } = trackSwiftOrderResult();

				const takerAuthority = authorityWallet.publicKey;
				const signingAuthority = preferredTradingSignerWallet.publicKey;
				const isDelegate = !preferredTradingSignerWallet.publicKey.equals(
					authorityWallet.publicKey
				);

				const auctionVersion = useFeatureFlagStore
					.getState()
					.flagEnabled('AUCTION_PARAMS_VERSION')
					? 2
					: 1;

				await createSwiftMarketOrder({
					driftClient,
					user: currentUserAccount.client,
					assetType: 'base', // we default to base order, since previous iteration also defaults to base
					marketIndex: perpTradeFormOutputProps.targetMarketIndex,
					direction,
					amount: baseAmountToUseBN,
					dlobServerHttpUrl: Env.dlobServerHttpUrl,
					bracketOrders,
					optionalAuctionParamsInputs,
					reduceOnly: perpTradeFormOutputProps.reduceOnly,
					userOrderId: nextUserOrderId,
					positionMaxLeverage: perpTradeFormOutputProps.displayLeverage,
					isolatedPositionDepositsOverride:
						perpTradeFormOutputProps.isolatedPositionDeposit
							? {
									mainDeposit: perpTradeFormOutputProps.isolatedPositionDeposit,
								}
							: undefined,
					highLeverageOptions: {
						numOfOpenHighLeverageSpots: highLeverageSpotsOpen,
					},
					callbacks: {
						onAuctionParamsFetched: (urlSearchParams, response) => {
							logOrderParams(
								marketId,
								currentUserAccount.pubKey,
								urlSearchParams,
								response
							);
						},
					},
					swiftOptions: {
						source: DRIFT_UI_SWIFT_ORDER_SOURCE,
						wallet: {
							signMessage: async (message: Uint8Array) => {
								return preferredTradingSignerWallet.signMessage(message);
							},
							takerAuthority,
							signingAuthority,
						},
						swiftServerUrl: Env.swiftServerUrl,
						userSigningSlotBuffer: Env.signedMessageOrderSlotBuffer,
						isDelegate,
						confirmationMultiplier: swiftConfirmationMultiplier,
						callbacks: {
							onOrderParamsMessagePrepped(orderParamsMessage) {
								appEventEmitter.emit('signedMsgOrderInit', {
									...signedMsgOrderToastEventProps,
									signedMsgOrderPayload: orderParamsMessage,
									isAutoSigned: isEmbeddedWalletThePreferredTradingSigner,
								});
							},
							onSigningSuccess(signedMessage, orderUuid, orderParamsMessage) {
								swiftDebugProps.swift_uuid = new TextDecoder('utf-8').decode(
									orderUuid
								);
								swiftDebugProps.swift_error_preppedParams.orderParams =
									JSON.stringify(orderParamsMessage);
								appEventEmitter.emit('signedMsgOrderSigned', {
									...signedMsgOrderToastEventProps,
									signedMsgOrderPayload: orderParamsMessage,
								});
								OrderFillTimeTracker.trackOrderSubmission(
									currentUserAccount?.client?.getUserAccount().nextOrderId ?? 0,
									Date.now(),
									orderParamsMessage.signedMsgOrderParams,
									'swift',
									currentUserAccount.pubKey.toString(),
									auctionVersion,
									digestSignature(Uint8Array.from(signedMessage)),
									oraclePrice.val
								);
							},
							onSigningExpiry(orderParamsMessage) {
								handleResult({ sent: false, retryWithDepositBuffer: false });

								appEventEmitter.emit('signedMsgOrderMessageSlotExpired', {
									...signedMsgOrderToastEventProps,
									signedMsgOrderPayload: orderParamsMessage,
								});
							},
							onSent(event) {
								handleResult({ sent: true, retryWithDepositBuffer: false });
								dlog(
									`market_orders`,
									`Sent signed message order with hash : ${event.hash}`
								);
							},
							onConfirmed(event) {
								appEventEmitter.emit('signedMsgOrderConfirmed', {
									...signedMsgOrderToastEventProps,
									signedMsgOrderPayload: event.orderParamsMessage,
									orderId: Number(event.orderId),
								});
							},
							onExpired() {
								handleResult({ sent: false, retryWithDepositBuffer: false });

								appEventEmitter.emit('signedMsgOrderExpired', {
									...signedMsgOrderToastEventProps,
								});
								DriftWindow.metricsBus.next({
									type: 'passthrough_metric',
									value: {
										swiftOrdersTimeout: 1,
									},
								});
							},
							onErrored(event) {
								// Check if this is an InsufficientCollateral error for an isolated position
								let shouldRetryWithDepositBuffer = false;
								if (
									event.message &&
									perpTradeFormOutputProps.isolatedPositionDeposit
								) {
									const anchorError = createAnchorErrorFromSwiftMessage(
										event.message
									);
									if (
										anchorError?.code === INSUFFICIENT_COLLATERAL_ERROR_CODE
									) {
										shouldRetryWithDepositBuffer = true;
									}
								}

								handleResult({
									sent: false,
									retryWithDepositBuffer: shouldRetryWithDepositBuffer,
								});

								logSwiftError(event, swiftDebugProps);

								if (
									event.message?.includes(
										SWIFT_ORDER_SIGNATURE_VERIFICATION_ERROR_MESSAGE
									)
								) {
									// Signature verification failed (possible ledger sign off chain error)
									appEventEmitter.emit(
										'signedMsgSignatureFailed',
										signedMsgOrderToastEventProps
									);
								} else {
									// Try to extract Drift error for better error messaging
									let formattedErrorMessage = event.message;
									if (event.message) {
										const anchorError = createAnchorErrorFromSwiftMessage(
											event.message
										);
										if (anchorError) {
											// Get the mapped error from DriftErrors for better error messages
											const mappedError = tryGetDriftErrorCode(anchorError);
											if (mappedError && mappedError.toast) {
												formattedErrorMessage = `${
													mappedError.toast.description || mappedError.msg
												}`;
											}
										}
									}

									// Always emit app event to maintain order tracking and use market order toast system
									appEventEmitter.emit('signedMsgOrderFailed', {
										...signedMsgOrderToastEventProps,
										message: formattedErrorMessage,
									});
								}

								if (event.status && event.status >= 400 && event.status < 500) {
									DriftWindow.metricsBus.next({
										type: 'passthrough_metric',
										value: {
											swiftOrdersBadRequests: 1,
										},
									});
								}
								if (event.status && event.status >= 500) {
									DriftWindow.metricsBus.next({
										type: 'passthrough_metric',
										value: {
											swiftOrdersServerErrors: 1,
										},
									});
								}
							},
						},
					},
				});

				const result = await resultPromise;

				return {
					sent: result.sent,
					swiftUuid: swiftDebugProps.swift_uuid,
					retryWithDepositBuffer: result.retryWithDepositBuffer,
				};
			} catch (e: unknown) {
				logSwiftError(e, swiftDebugProps);

				if (isLedgerSignMessageError(e as AnchorError)) {
					appEventEmitter.emit(
						'signedMsgSignatureFailed',
						signedMsgOrderToastEventProps
					);
				} else {
					appEventEmitter.emit(
						'signedMsgOrderFailed',
						signedMsgOrderToastEventProps
					);
				}

				return { sent: false, swiftUuid: '', retryWithDepositBuffer: false };
			}
		},
		[
			driftClient,
			currentUserAccount,
			wallet,
			swiftConfirmationMultiplier,
			appEventEmitter,
			authorityWallet,
			preferredTradingSignerWallet,
			isEmbeddedWalletThePreferredTradingSigner,
			logSwiftError,
			getOraclePrice,
			getMarkPrice,
			captureEvent,
		]
	);

	const openSwiftLimitOrder = useCallback(
		async (
			perpTradeFormOutputProps: PerpTradeFormOutputProps & {
				leadSide: 'base' | 'quote';
			},
			highLeverageSpotsOpen?: number
		): Promise<{
			sent: boolean;
			swiftUuid: string;
			retryWithDepositBuffer: boolean;
		}> => {
			invariant(driftClient, 'driftClient is required');
			invariant(currentUserAccount?.client, 'currentUserAccount is required');
			invariant(authorityWallet, 'authorityWallet is required');
			invariant(
				preferredTradingSignerWallet,
				'preferredTradingSignerWallet is required'
			);

			const {
				direction,
				baseAmountBigNum,
				bracketOrders,
				optionalAuctionParamsInputs,
			} = ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps(
				perpTradeFormOutputProps,
				{
					isOracleOrder: false, // we don't use oracle order parameters for limit auction orders
				}
			);

			const orderType = perpTradeFormOutputProps.orderType as UIOrderType;
			const uiMarket = UIMarket.createPerpMarket(
				perpTradeFormOutputProps.targetMarketIndex
			);
			const settings = syncGetCurrentSettings() as UserSettings;
			const oraclePrice = getOraclePrice(uiMarket.marketId);
			const markPrice = getMarkPrice(uiMarket.marketId).toNum();

			const nextUserOrderId = getNextUserOrderIdForCurrentUser();
			const toastId = `${orderType}-${
				perpTradeFormOutputProps.side
			}-${Date.now()}`;

			const orderTypeString = orderType === 'limit' ? 'Limit Order' : 'Order'; // For limit orders we specify "Limit Order placed", otherwise it can just be "Order placed"

			const limitPrice = BigNum.fromPrint(
				perpTradeFormOutputProps.priceBoxStringValue ?? '0',
				PRICE_PRECISION_EXP
			);

			notify({
				type: isEmbeddedWalletThePreferredTradingSigner
					? 'awaiting'
					: 'awaiting_wallet_confirmation',
				id: toastId,
				message: isEmbeddedWalletThePreferredTradingSigner
					? 'Signing order message...'
					: undefined,
				updatePrevious: true,
				showUntilCancelled: true,
				timeoutStartMs: Date.now(),
				timeoutEndMs:
					Date.now() +
					(MINIMUM_SWIFT_ORDER_SIGNING_EXPIRATION_BUFFER_SLOTS -
						SWIFT_ORDER_SIGNING_EXPIRATION_BUFFER_SLOTS) *
						SLOT_TIME_ESTIMATE_MS,
			});

			// safety check for max leverage flag usage
			const useMaxLeverage = checkMaxLeverageFlagUsage(
				!!perpTradeFormOutputProps.maxLeverageSelected,
				perpTradeFormOutputProps.targetMarketIndex,
				baseAmountBigNum,
				oraclePrice,
				currentUserAccount.client,
				direction,
				perpTradeFormOutputProps.displayLeverage,
				highLeverageSpotsOpen
			);
			const baseAmountToUseBN = useMaxLeverage
				? MAX_LEVERAGE_ORDER_SIZE
				: baseAmountBigNum.val;

			const swiftDebugProps = getSwiftDebugProps(
				perpTradeFormOutputProps,
				currentUserAccount.client,
				'', // uuid populated in onSigningSuccess
				uiMarket.symbol,
				oraclePrice.toNum(),
				markPrice,
				settings,
				currentSlotRef.current,
				bracketOrders
			);

			try {
				const { handleResult, resultPromise } = trackSwiftOrderResult();

				const takerAuthority = authorityWallet.publicKey;
				const signingAuthority = preferredTradingSignerWallet.publicKey;
				const isDelegate = !authorityWallet.publicKey.equals(
					preferredTradingSignerWallet.publicKey
				);

				await createSwiftLimitOrder({
					driftClient,
					user: currentUserAccount.client,
					assetType: 'base', // we default to base order, since previous iteration also defaults to base
					marketIndex: perpTradeFormOutputProps.targetMarketIndex,
					userOrderId: nextUserOrderId,
					direction,
					amount: baseAmountToUseBN,
					positionMaxLeverage: perpTradeFormOutputProps.displayLeverage,
					isolatedPositionDepositsOverride:
						perpTradeFormOutputProps.isolatedPositionDeposit
							? {
									mainDeposit: perpTradeFormOutputProps.isolatedPositionDeposit,
								}
							: undefined,
					highLeverageOptions: {
						numOfOpenHighLeverageSpots: highLeverageSpotsOpen,
					},
					reduceOnly: perpTradeFormOutputProps.reduceOnly,
					postOnly: perpTradeFormOutputProps.postOnly
						? PostOnlyParams.MUST_POST_ONLY
						: PostOnlyParams.NONE,
					orderConfig: {
						orderType: 'limit',
						limitPrice: limitPrice.val,
						bracketOrders,
						limitAuction: {
							// Post-only orders cannot have auction params per protocol validation.
							// If a crossing post-only order is submitted, it will fail with an appropriate error message.
							enable: !perpTradeFormOutputProps.postOnly,
							dlobServerHttpUrl: Env.dlobServerHttpUrl,
							auctionStartPriceOffset:
								TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
									settings?.auctionStartPriceOffset as number | 'marketBased',
									uiMarket.marketId
								),
							oraclePrice: oraclePrice.val,
							optionalLimitAuctionParams: optionalAuctionParamsInputs,
						},
					},
					swiftOptions: {
						source: DRIFT_UI_SWIFT_ORDER_SOURCE,
						wallet: {
							signMessage: async (message: Uint8Array) => {
								return preferredTradingSignerWallet.signMessage(message);
							},
							takerAuthority,
							signingAuthority,
						},
						swiftServerUrl: Env.swiftServerUrl,
						userSigningSlotBuffer: Env.signedMessageOrderSlotBuffer,
						isDelegate,
						confirmationMultiplier: swiftConfirmationMultiplier,
						callbacks: {
							onSigningSuccess(_signedMessage, orderUuid) {
								swiftDebugProps.swift_uuid = new TextDecoder('utf-8').decode(
									orderUuid
								);
								notify({
									type: 'success',
									message: 'Order message signed',
									id: toastId,
									subDescription: '',
									updatePrevious: true,
								});
							},
							onSigningExpiry() {
								handleResult({ sent: false, retryWithDepositBuffer: false });
								notify({
									type: 'error',
									message: 'Message Expired',
									description: 'Message expired for signing. Slot is too old.',
									id: toastId,
									subDescription: '',
									updatePrevious: true,
								});
							},
							onSent(event) {
								handleResult({ sent: true, retryWithDepositBuffer: false });
								dlog(
									`market_orders`,
									`Sent signed message order with hash : ${event.hash}`
								);
							},
							onConfirmed() {
								notify({
									type: 'success',
									message: `${orderTypeString} placed`,
									id: toastId,
									subDescription: '',
									updatePrevious: true,
								});
							},
							onExpired() {
								handleResult({ sent: false, retryWithDepositBuffer: false });
								notify({
									type: 'error',
									message: 'Order message expired',
									description:
										'Order message expired - limit order was likely not placed on-chain.',
									id: toastId,
									subDescription: '',
									updatePrevious: true,
								});
								DriftWindow.metricsBus.next({
									type: 'passthrough_metric',
									value: {
										swiftOrdersTimeout: 1,
									},
								});
							},
							onErrored(event) {
								// Check if this is an InsufficientCollateral error for an isolated position
								let shouldRetryWithDepositBuffer = false;
								if (
									event.message &&
									perpTradeFormOutputProps.isolatedPositionDeposit
								) {
									const parsedError = createAnchorErrorFromSwiftMessage(
										event.message
									);
									if (
										parsedError?.code === INSUFFICIENT_COLLATERAL_ERROR_CODE
									) {
										shouldRetryWithDepositBuffer = true;
									}
								}

								handleResult({
									sent: false,
									retryWithDepositBuffer: shouldRetryWithDepositBuffer,
								});
								logSwiftError(event, swiftDebugProps);

								if (event.message) {
									const anchorError = createAnchorErrorFromSwiftMessage(
										event.message
									);

									if (
										event.message?.includes(
											SWIFT_ORDER_SIGNATURE_VERIFICATION_ERROR_MESSAGE
										)
									) {
										// Signature verification failed (possible ledger sign off chain error)
										notify({
											type: 'error',
											message: 'Order message failed',
											id: toastId,
											subDescription:
												LEDGER_SIGN_OFF_CHAIN_MESSAGE_ERROR_MESSAGE,
											updatePrevious: true,
										});
									} else if (anchorError && wallet.wallet?.adapter) {
										handleTxError(anchorError, toastId, 'Order Failed');
									} else {
										notify({
											type: 'error',
											message: 'Order message failed',
											id: toastId,
											subDescription: event.message,
											updatePrevious: true,
										});
									}
								} else {
									notify({
										type: 'error',
										message: 'Order message failed',
										id: toastId,
										subDescription: '',
										updatePrevious: true,
									});
								}

								if (event.status && event.status >= 400 && event.status < 500) {
									DriftWindow.metricsBus.next({
										type: 'passthrough_metric',
										value: {
											swiftOrdersBadRequests: 1,
										},
									});
								}
								if (event.status && event.status >= 500) {
									DriftWindow.metricsBus.next({
										type: 'passthrough_metric',
										value: {
											swiftOrdersServerErrors: 1,
										},
									});
								}
							},
						},
					},
				});

				const result = await resultPromise;

				return {
					sent: result.sent,
					swiftUuid: swiftDebugProps.swift_uuid,
					retryWithDepositBuffer: result.retryWithDepositBuffer,
				};
			} catch (e: unknown) {
				logSwiftError(e, swiftDebugProps);
				handleTxError(e, toastId);

				return { sent: false, swiftUuid: '', retryWithDepositBuffer: false };
			}
		},
		[
			driftClient,
			currentUserAccount,
			wallet,
			appEventEmitter,
			swiftConfirmationMultiplier,
			authorityWallet,
			preferredTradingSignerWallet,
			isEmbeddedWalletThePreferredTradingSigner,
			logSwiftError,
			getOraclePrice,
			getMarkPrice,
			handleTxError,
			captureEvent,
		]
	);

	return { openSwiftMarketOrder, openSwiftLimitOrder };
};

function getSwiftDebugProps(
	perpTradeFormOutputProps: PerpTradeFormOutputProps & {
		leadSide: 'base' | 'quote';
	},
	user: User,
	swiftUuid: string,
	marketName: string,
	oraclePrice: number,
	markPrice: number,
	settings: UserSettings,
	currentSlot: number,
	bracketOrders: ReturnType<
		typeof ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps
	>['bracketOrders'],
	signedMsgOrderToastEventProps?: SignedMsgToastEvent
) {
	// Calculate display leverage for margin ratio
	const marketIndex = perpTradeFormOutputProps.targetMarketIndex;

	const swiftDebugProps = {
		swift_error_tradeFormProps: {
			targetMarketIndex: marketIndex,
			oraclePrice,
			markPrice,
			currentSettings: JSON.stringify(settings),
			side: perpTradeFormOutputProps.side,
			baseSizeStringValue: perpTradeFormOutputProps.baseSizeStringValue,
			orderType: perpTradeFormOutputProps.orderType,
			bracketOrders: perpTradeFormOutputProps.bracketOrders,
			reduceOnly: perpTradeFormOutputProps.reduceOnly,
			slippageTolerance: perpTradeFormOutputProps.slippageTolerance,
			maxLeverageSelected: perpTradeFormOutputProps.maxLeverageSelected,
			leverageAfterTrade: perpTradeFormOutputProps.leverageAfterTrade,
			priceImpact: JSON.stringify(perpTradeFormOutputProps.priceImpact),
			displayLeverage: perpTradeFormOutputProps.displayLeverage,
			maxTradeSizeWithoutHLM: BigNum.from(
				user.getMaxTradeSizeUSDCForPerp(
					marketIndex,
					perpTradeFormOutputProps.side === 'sell'
						? PositionDirection.SHORT
						: PositionDirection.LONG,
					undefined,
					false, // enterHighLeverageMode = false
					perpTradeFormOutputProps.maxLeverageSelected
						? COMMON_UI_UTILS.convertLeverageToMarginRatio(
								perpTradeFormOutputProps.displayLeverage
							)
						: undefined
				).tradeSize,
				QUOTE_PRECISION_EXP
			).toNum(),
			currentLeverage: BigNum.from(user.getLeverage(true), FOUR).toNum(),
			freeCollateral: BigNum.from(
				user.getFreeCollateral(),
				QUOTE_PRECISION_EXP
			).toNum(),
			totalCollateral: BigNum.from(
				user.getTotalCollateral(),
				QUOTE_PRECISION_EXP
			).toNum(),
			initialMarginRequirement: BigNum.from(
				user.getInitialMarginRequirement(),
				QUOTE_PRECISION_EXP
			).toNum(),
			currentUiSlot: currentSlot,
			spotPositions: JSON.stringify(user.getActiveSpotPositions()),
			perpPositions: JSON.stringify(user.getActivePerpPositions()),
		},
		swift_error_preppedParams: {
			orderParams: '', // to be populated in onSigningSuccess callback
			bracketOrdersParams: JSON.stringify(bracketOrders),
			userAccountPublicKey: user.getUserAccountPublicKey().toBase58(),
			signedMsgOrderToastEventProps: JSON.stringify(
				signedMsgOrderToastEventProps
			),
			entryPrice:
				perpTradeFormOutputProps.priceImpact?.entryPrice?.toNumber() ?? 0,
			marketName: marketName,
			subAccountId: user.getUserAccount().subAccountId,
		},
		swift_uuid: swiftUuid,
	};

	return swiftDebugProps;
}
